import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Container,
  Chip,
  Tooltip,
  Skeleton,
  TextField,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import { alpha, darken } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";

import {
  fetchAppointments,
  fetchPendingAppointments,
  deleteAppointment,
  setAppointmentTime,
  updateAppointmentStatus,
} from "../redux/appointmentSlice";

const SKELETON_ROWS = 6;

const TIME_SLOTS = [
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
  "18:30",
  "19:00",
];

const AdminAppointmentsPage = () => {
  const dispatch = useDispatch();
  const {
    items: appointments = [],
    pendingItems = [],
    loading,
    settingTime,
  } = useSelector((s) => s.appointments);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [timeDialog, setTimeDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    dispatch(fetchAppointments());
    dispatch(fetchPendingAppointments());
  }, [dispatch]);

  const handleDelete = async () => {
    await dispatch(deleteAppointment(deleteId));
    setConfirmDelete(false);
    setDeleteId(null);
  };

  const handleSetTime = async () => {
    if (!selectedTime) {
      alert("Lütfen bir saat seçin");
      return;
    }

    try {
      await dispatch(
        setAppointmentTime({
          id: selectedAppointment._id,
          time: selectedTime,
        })
      ).unwrap();

      alert("✅ Randevu onaylandı ve müşteriye SMS gönderildi!");
      setTimeDialog(false);
      setSelectedAppointment(null);
      setSelectedTime("");

      // Listeyi yenile
      dispatch(fetchAppointments());
      dispatch(fetchPendingAppointments());
    } catch (err) {
      alert("❌ Hata: " + err);
    }
  };

  const getStatusChip = (status, time) => {
    if (!time) {
      return (
        <Chip
          size="small"
          icon={<PendingIcon sx={{ fontSize: 16 }} />}
          label="Bekliyor"
          sx={{
            bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
            color: "warning.main",
            fontWeight: 600,
          }}
        />
      );
    }

    const statusConfig = {
      pending: {
        label: "Bekliyor",
        color: "warning",
        icon: <PendingIcon sx={{ fontSize: 16 }} />,
      },
      confirmed: {
        label: "Onaylandı",
        color: "success",
        icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
      },
      completed: {
        label: "Tamamlandı",
        color: "info",
        icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
      },
      cancelled: {
        label: "İptal",
        color: "error",
        icon: <DeleteIcon sx={{ fontSize: 16 }} />,
      },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        size="small"
        icon={config.icon}
        label={config.label}
        sx={{
          bgcolor: (t) => alpha(t.palette[config.color].main, 0.12),
          color: `${config.color}.main`,
          fontWeight: 600,
        }}
      />
    );
  };

  const displayAppointments =
    tabValue === 0 ? pendingItems : appointments.filter((a) => a.time);

  return (
    <Container
      maxWidth="lg"
      sx={{ py: { xs: 4, md: 5 }, position: "relative" }}
    >
      {/* Ambient background */}
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: -1,
          background: (t) =>
            t.palette.mode === "dark"
              ? `radial-gradient(circle at 20% 18%, ${alpha(
                  t.palette.primary.main,
                  0.18
                )}, transparent 60%), linear-gradient(135deg, ${
                  t.palette.background.default
                }, ${t.palette.background.default})`
              : `radial-gradient(circle at 20% 18%, ${alpha(
                  t.palette.primary.light,
                  0.45
                )}, transparent 65%), linear-gradient(135deg, ${
                  t.palette.background.default
                }, ${t.palette.background.paper})`,
        }}
      />

      {/* HEADER */}
      <Paper
        elevation={0}
        sx={(t) => ({
          mb: 4,
          p: 3,
          borderRadius: 4,
          display: "flex",
          flexDirection: "column",
          gap: 1.4,
          backdropFilter: "blur(18px)",
          background: alpha(t.palette.background.paper, 0.85),
          border: `1px solid ${alpha(t.palette.divider, 0.3)}`,
          position: "relative",
          overflow: "hidden",
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: `linear-gradient(120deg, ${alpha(
              t.palette.primary.main,
              0.09
            )}, transparent 60%)`,
            pointerEvents: "none",
          },
        })}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={(t) => ({
              width: 52,
              height: 52,
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: alpha(t.palette.primary.main, 0.15),
              border: `1px solid ${alpha(t.palette.primary.main, 0.25)}`,
              flexShrink: 0,
            })}
          >
            <EventIcon color="primary" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.5px",
                lineHeight: 1.08,
              }}
            >
              Randevu Yönetimi
            </Typography>
            <Box sx={{ mt: 0.6, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                size="small"
                icon={<PendingIcon sx={{ fontSize: 18 }} />}
                label={`${pendingItems.length} Bekliyor`}
                sx={{
                  height: 30,
                  fontWeight: 600,
                  bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
                  color: "warning.main",
                  ".MuiChip-label": { px: 1.2 },
                }}
              />
              <Chip
                size="small"
                icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                label={`${appointments.filter((a) => a.time).length} Onaylı`}
                sx={{
                  height: 30,
                  fontWeight: 600,
                  bgcolor: (t) => alpha(t.palette.success.main, 0.12),
                  color: "success.main",
                  ".MuiChip-label": { px: 1.2 },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* TABS */}
      <Paper
        elevation={0}
        sx={(t) => ({
          mb: 2,
          borderRadius: 4,
          backdropFilter: "blur(14px)",
          background: alpha(t.palette.background.paper, 0.85),
          border: `1px solid ${alpha(t.palette.divider, 0.3)}`,
          overflow: "hidden",
        })}
      >
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          sx={{ px: 2 }}
        >
          <Tab
            label="Bekleyen Randevular"
            icon={<PendingIcon />}
            iconPosition="start"
          />
          <Tab
            label="Onaylanmış Randevular"
            icon={<CheckCircleIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* TABLE */}
      <Paper
        elevation={0}
        sx={(t) => ({
          borderRadius: 4,
          backdropFilter: "blur(14px)",
          background: alpha(t.palette.background.paper, 0.85),
          border: `1px solid ${alpha(t.palette.divider, 0.3)}`,
          overflow: "hidden",
        })}
      >
        <TableContainer sx={{ maxHeight: 620 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 70 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Ad Soyad</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Telefon</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Saat</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 150 }}>
                  İşlemler
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell sx={{ py: 1.8 }}>
                      <Skeleton width={40} height={28} />
                    </TableCell>
                    <TableCell sx={{ py: 1.8 }}>
                      <Skeleton width="45%" height={20} />
                    </TableCell>
                    <TableCell sx={{ py: 1.8 }}>
                      <Skeleton width="35%" height={20} />
                    </TableCell>
                    <TableCell sx={{ py: 1.8 }}>
                      <Skeleton width="30%" height={20} />
                    </TableCell>
                    <TableCell sx={{ py: 1.8 }}>
                      <Skeleton width="30%" height={20} />
                    </TableCell>
                    <TableCell sx={{ py: 1.8 }}>
                      <Skeleton width="30%" height={20} />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.8 }}>
                      <Skeleton width={80} height={30} />
                    </TableCell>
                  </TableRow>
                ))}

              {!loading && displayAppointments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 8 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        opacity: 0.75,
                      }}
                    >
                      <EventIcon color="disabled" sx={{ fontSize: 50 }} />
                      <Typography variant="body2" color="text.secondary">
                        {tabValue === 0
                          ? "Bekleyen randevu yok."
                          : "Onaylanmış randevu yok."}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                displayAppointments.map((app, index) => (
                  <TableRow
                    key={app._id}
                    hover
                    sx={{
                      transition: ".25s",
                      "&:hover": {
                        backgroundColor: (t) =>
                          alpha(t.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <TableCell sx={{ py: 1.6 }}>
                      <Typography
                        sx={(t) => ({
                          width: 34,
                          height: 30,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          fontSize: 13,
                          bgcolor: alpha(t.palette.primary.main, 0.12),
                          color: "primary.main",
                        })}
                      >
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.6 }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        {app.fullName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.6 }}>{app.phone}</TableCell>
                    <TableCell sx={{ py: 1.6 }}>
                      {new Date(app.date).toLocaleDateString("tr-TR")}
                    </TableCell>
                    <TableCell sx={{ py: 1.6 }}>{app.time || "-"}</TableCell>
                    <TableCell sx={{ py: 1.6 }}>
                      {getStatusChip(app.status, app.time)}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.6 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                        }}
                      >
                        {!app.time && (
                          <Tooltip title="Saat Belirle" arrow>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedAppointment(app);
                                setTimeDialog(true);
                              }}
                              sx={(t) => ({
                                width: 36,
                                height: 36,
                                borderRadius: 3,
                                color: "primary.main",
                                bgcolor: alpha(t.palette.primary.main, 0.12),
                                "&:hover": {
                                  bgcolor: alpha(t.palette.primary.main, 0.2),
                                },
                              })}
                            >
                              <AccessTimeIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Sil" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeleteId(app._id);
                              setConfirmDelete(true);
                            }}
                            sx={(t) => ({
                              width: 36,
                              height: 36,
                              borderRadius: 3,
                              color: "error.main",
                              bgcolor: alpha(t.palette.error.main, 0.12),
                              "&:hover": {
                                bgcolor: alpha(t.palette.error.main, 0.2),
                              },
                            })}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Set Time Dialog */}
      <Dialog
        open={timeDialog}
        onClose={() => !settingTime && setTimeDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            width: "100%",
            maxWidth: 450,
            p: 0,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Randevu Saati Belirle
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedAppointment?.fullName} -{" "}
            {selectedAppointment &&
              new Date(selectedAppointment.date).toLocaleDateString("tr-TR")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Saat Seçin"
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            sx={{ mt: 2 }}
          >
            {TIME_SLOTS.map((time) => (
              <MenuItem key={time} value={time}>
                {time}
              </MenuItem>
            ))}
          </TextField>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            * Randevu onaylandığında müşteriye otomatik SMS gönderilecektir.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setTimeDialog(false)}
            disabled={settingTime}
            sx={{ textTransform: "none", borderRadius: 3 }}
          >
            İptal
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSetTime}
            disabled={settingTime || !selectedTime}
            sx={(t) => ({
              textTransform: "none",
              borderRadius: 3,
              fontWeight: 600,
              background: `linear-gradient(90deg, ${
                t.palette.primary.main
              }, ${darken(t.palette.primary.main, 0.18)})`,
            })}
          >
            {settingTime ? "Onaylanıyor..." : "Onayla ve SMS Gönder"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            width: "100%",
            maxWidth: 400,
            p: 0,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Randevuyu Sil
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Bu işlem geri alınamaz
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography fontSize={13}>
            Seçili randevuyu kalıcı olarak silmek istiyor musunuz?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setConfirmDelete(false)}
            sx={{ textTransform: "none", borderRadius: 3 }}
          >
            Vazgeç
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            sx={(t) => ({
              textTransform: "none",
              borderRadius: 3,
              fontWeight: 600,
              background: `linear-gradient(90deg, ${
                t.palette.error.main
              }, ${darken(t.palette.error.main, 0.18)})`,
            })}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminAppointmentsPage;
